import { ValidationErrors } from '@angular/forms';

export class CustomValidator {
  static readonly MAX_IMAGE_FILE_SIZE_IN_MB = 5;
  static readonly MAX_VIDEO_FILE_SIZE_IN_MB = 500;

  static validateImageFile(file: File): Promise<ValidationErrors | null> {
    const maxSize = this.MAX_IMAGE_FILE_SIZE_IN_MB * 1024 * 1024;

    if (!file) {
      return Promise.resolve({ required: 'File is required' });
    }

    if (file.size > maxSize) {
      return Promise.resolve({
        fileSize: `File size exceeds ${this.MAX_IMAGE_FILE_SIZE_IN_MB}MB`,
      });
    }

    if (file.type === 'image/svg+xml') {
      return this.validateSvgFile(file);
    } else {
      return this.validateBinaryImageFile(file);
    }
  }

  private static validateBinaryImageFile(
    file: File,
  ): Promise<ValidationErrors | null> {
    const allowedMagicNumbers: { [key: string]: string[] } = {
      'image/png': ['89504E47'],
      'image/jpeg': [
        'FFD8FFE0',
        'FFD8FFE1',
        'FFD8FFE2',
        'FFD8FFE3',
        'FFD8FFE8',
      ],
      'image/gif': ['47494638'],
      'image/webp': ['52494646'],
      'image/bmp': ['424D'],
      'image/tiff': ['49492A00', '4D4D002A'],
      'image/x-icon': ['00000100', '00000200'],
      'image/heif': ['66747970'],
      'image/heic': ['66747970'],
    };

    return new Promise<ValidationErrors | null>((resolve) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const arrayBuffer = e.target.result as ArrayBuffer;
        const uintArray = new Uint8Array(arrayBuffer).subarray(0, 8);
        let magicNumber = '';

        for (let i = 0; i < uintArray.length; i++) {
          magicNumber += uintArray[i].toString(16).padStart(2, '0');
        }

        magicNumber = magicNumber.toUpperCase();

        const isValidImage = Object.values(allowedMagicNumbers).some(
          (numbers) => numbers.some((num) => magicNumber.startsWith(num)),
        );

        resolve(
          isValidImage ? null : { invalidFileType: 'Only images are allowed' },
        );
      };

      reader.readAsArrayBuffer(file.slice(0, 8));
    });
  }

  private static validateSvgFile(file: File): Promise<ValidationErrors | null> {
    return new Promise<ValidationErrors | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        const svgRegex = /<svg[\s\S]*?>[\s\S]*?<\/svg>/i;
        const scriptRegex = /<script[\s\S]*?>[\s\S]*?<\/script>/i;

        if (!svgRegex.test(content)) {
          resolve({ invalidFileType: 'Invalid SVG content' });
        } else if (scriptRegex.test(content)) {
          resolve({ invalidFileType: 'SVG contains forbidden scripts' });
        } else {
          resolve(null);
        }
      };

      reader.readAsText(file);
    });
  }

  static validateVideoFile(file: File): Promise<ValidationErrors | null> {
    const maxSize = this.MAX_VIDEO_FILE_SIZE_IN_MB * 1024 * 1024;

    if (!file) {
      return Promise.resolve({ required: 'Video file is required' });
    }

    if (file.size > maxSize) {
      return Promise.resolve({
        fileSize: `Video file size exceeds ${this.MAX_VIDEO_FILE_SIZE_IN_MB}MB`,
      });
    }

    return this.validateBinaryVideoFile(file);
  }

  private static validateBinaryVideoFile(
    file: File,
  ): Promise<ValidationErrors | null> {
    return new Promise<ValidationErrors | null>((resolve) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const arrayBuffer = e.target.result as ArrayBuffer;
        const uintArray = new Uint8Array(arrayBuffer);

        let hexString = '';
        for (let i = 0; i < Math.min(32, uintArray.length); i++) {
          hexString += uintArray[i].toString(16).padStart(2, '0');
        }
        hexString = hexString.toUpperCase();

        const isWebm = hexString.startsWith('1A45DFA3');
        const isAvi =
          hexString.startsWith('52494646') &&
          hexString.substring(8, 16) === '41564920';
        const isMp4Mov = hexString.includes('66747970');
        const isMov = hexString.includes('6D6F6F76');
        const isValidVideo = isWebm || isAvi || isMp4Mov || isMov;

        resolve(
          isValidVideo
            ? null
            : {
                invalidFileType:
                  'Please select a valid video file (MP4, AVI, MOV, WebM)',
              },
        );
      };

      reader.readAsArrayBuffer(file.slice(0, 32));
    });
  }
}
